import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {Button, FormControl, Glyphicon, Label, Table} from "react-bootstrap";
import {CopyToClipboard} from "react-copy-to-clipboard";
import {createSim, getSims, getUnits} from "../utils/bedrockAPI";
import {formatPhoneNumber, getUser} from "../utils/Common";
import {apiErrorMessage, formatDate, sticker} from "../utils/unitDisplay";
import {
    SIM_CARRIERS,
    SIM_STATUSES,
    findUnitBySticker,
    isHardwareSim,
    simStatusStyle,
} from "../utils/simDisplay";
import SimExpandPanel from "./SimExpandPanel";
import "./Units.css";
import "./Admin.css";

const LIST_COLUMNS = 9;

export default function Sims({history}) {
    const [sims, setSims] = useState([]);
    const [units, setUnits] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("INVENTORY");
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [error, setError] = useState(null);
    const [creating, setCreating] = useState(false);
    const [newIccid, setNewIccid] = useState("");
    const [newPhone, setNewPhone] = useState("");
    const [newCarrier, setNewCarrier] = useState("ATT");
    const [newSticker, setNewSticker] = useState("");

    const unitsById = {};
    (units || []).forEach((unit) => {
        if (unit?.unitId) unitsById[unit.unitId] = unit;
    });

    const loadData = async ({silent = false} = {}) => {
        if (!silent) setLoading(true);
        setError(null);
        try {
            const [simRows, unitRows] = await Promise.all([
                getSims(),
                getUnits().catch(() => []),
            ]);
            const nextSims = (Array.isArray(simRows) ? simRows : []).filter(isHardwareSim);
            setSims(nextSims);
            setUnits(Array.isArray(unitRows) ? unitRows : []);
            return nextSims;
        } catch (err) {
            setError(apiErrorMessage(err));
            setSims([]);
            return [];
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        if (getUser().role !== "ADMIN") {
            history.push("/dashboard");
            return;
        }
        loadData();
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newIccid.trim() && !newPhone.trim()) {
            setError("Enter an ICCID or a phone number.");
            return;
        }
        let unitId = null;
        if (newSticker.trim()) {
            const found = findUnitBySticker(units, newSticker);
            if (!found.unit) {
                setError(
                    found.matches.length > 1
                        ? "That sticker matches more than one unit."
                        : "No unit found for that sticker."
                );
                return;
            }
            unitId = found.unit.unitId;
        }
        setCreating(true);
        setError(null);
        try {
            const created = await createSim({
                iccid: newIccid.trim() || null,
                phoneNumber: newPhone.trim() || null,
                carrier: newCarrier,
                status: unitId ? "ASSIGNED" : "INVENTORY",
                unitId,
            });
            await loadData({silent: true});
            setExpandedId(created.simId);
            setStatusFilter(unitId ? "ASSIGNED" : "INVENTORY");
            setNewIccid("");
            setNewPhone("");
            setNewCarrier("ATT");
            setNewSticker("");
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setCreating(false);
    };

    const handleSimPatched = (updated) => {
        if (!updated?.simId) return;
        setSims((prev) => prev.map((row) => (row.simId === updated.simId ? {...row, ...updated} : row)));
        const nextStatus = updated.status || (updated.unitId ? "ASSIGNED" : "INVENTORY");
        setStatusFilter((prev) => (prev && nextStatus !== prev ? nextStatus : prev));
    };

    const term = searchTerm.trim().toLowerCase();
    const filtered = sims.filter((sim) => {
        if (!isHardwareSim(sim)) return false;
        if (statusFilter && (sim.status || "").toUpperCase() !== statusFilter) return false;
        if (!term) return true;
        const unit = sim.unitId ? unitsById[sim.unitId] : null;
        const hay = [
            sim.simId,
            sim.iccid,
            sim.phoneNumber,
            sim.carrier,
            sim.status,
            sim.imei,
            sim.notes,
            sim.unitId,
            unit ? sticker(unit.unitId) : "",
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
        return hay.includes(term);
    });

    return (
        <div className="units-page admin-page">
            <header className="admin-header">
                <nav className="brand-breadcrumb">
                    <Link to="/admin">Admin</Link>
                    <span className="brand-breadcrumb-sep">/</span>
                    <span className="brand-breadcrumb-current">SIMs</span>
                </nav>
                <h1 className="admin-title">SIM inventory</h1>
                <p className="units-subtitle">
                    SIMs belong to the physical unit, not the customer cap. Assign a drawer SIM to a unit sticker; CapSoftware still reads the mirrored phone on the current cap.
                </p>
            </header>

            <form className="units-note-form" onSubmit={handleCreate}>
                <FormControl
                    type="text"
                    placeholder="ICCID"
                    value={newIccid}
                    onChange={(e) => setNewIccid(e.target.value)}
                    style={{flex: "1 1 180px"}}
                />
                <FormControl
                    type="text"
                    placeholder="Phone"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    style={{flex: "1 1 140px"}}
                />
                <FormControl
                    componentClass="select"
                    value={newCarrier}
                    onChange={(e) => setNewCarrier(e.target.value)}
                    className="units-note-type"
                >
                    {SIM_CARRIERS.map((name) => (
                        <option key={name} value={name}>
                            {name}
                        </option>
                    ))}
                </FormControl>
                <FormControl
                    type="text"
                    placeholder="Assign to sticker (optional)"
                    value={newSticker}
                    onChange={(e) => setNewSticker(e.target.value)}
                    style={{flex: "0 1 200px"}}
                />
                <Button type="submit" bsStyle="primary" bsSize="small" disabled={creating}>
                    <Glyphicon glyph="plus" /> {creating ? "Adding…" : "Add SIM"}
                </Button>
            </form>

            <div className="units-toolbar">
                <FormControl
                    type="text"
                    placeholder="Search ICCID, phone, carrier, sticker…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="units-search-input"
                />
                <FormControl
                    componentClass="select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="units-status-filter"
                >
                    <option value="">All statuses</option>
                    {SIM_STATUSES.map((status) => (
                        <option key={status} value={status}>
                            {status}
                        </option>
                    ))}
                </FormControl>
                <Button
                    bsStyle="default"
                    onClick={() => loadData({silent: sims.length > 0})}
                    disabled={loading && sims.length === 0}
                >
                    <Glyphicon glyph="refresh" />
                </Button>
            </div>

            {error && <div className="units-error">{error}</div>}
            {loading && sims.length === 0 && <div className="units-loading">Loading SIMs…</div>}
            {(!loading || sims.length > 0) && (
                <>
                    <div className="units-count">
                        {filtered.length} SIM{filtered.length === 1 ? "" : "s"}
                        {statusFilter ? ` · ${statusFilter}` : ""}
                    </div>
                    {filtered.length === 0 ? (
                        <div className="units-empty">No SIMs match.</div>
                    ) : (
                        <div className="units-table-wrapper">
                            <Table striped condensed className="units-table">
                                <thead>
                                    <tr>
                                        <th className="units-copy-cell" />
                                        <th />
                                        <th>ICCID</th>
                                        <th>Phone</th>
                                        <th>Carrier</th>
                                        <th>Status</th>
                                        <th>Unit</th>
                                        <th>Notes</th>
                                        <th>Added</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((sim) => {
                                        const expanded = expandedId === sim.simId;
                                        const phone = sim.phoneNumber
                                            ? formatPhoneNumber(sim.phoneNumber) || sim.phoneNumber
                                            : "—";
                                        return (
                                            <React.Fragment key={sim.simId}>
                                                <tr
                                                    className={expanded ? "units-row-open" : "units-row"}
                                                    onClick={() => setExpandedId(expanded ? null : sim.simId)}
                                                >
                                                    <td
                                                        className="units-copy-cell"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <CopyToClipboard text={sim.iccid || sim.simId}>
                                                            <Button
                                                                bsSize="xsmall"
                                                                bsStyle="default"
                                                                title="Copy ICCID or SIM ID"
                                                            >
                                                                <Glyphicon glyph="duplicate" />
                                                            </Button>
                                                        </CopyToClipboard>
                                                    </td>
                                                    <td className="units-expand-caret">
                                                        <Glyphicon glyph={expanded ? "chevron-down" : "chevron-right"} />
                                                    </td>
                                                    <td className="units-sticker">{sim.iccid || "—"}</td>
                                                    <td>{phone}</td>
                                                    <td>{sim.carrier || "—"}</td>
                                                    <td>
                                                        <Label bsStyle={simStatusStyle(sim.status)}>
                                                            {sim.status || "—"}
                                                        </Label>
                                                    </td>
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        {sim.unitId ? (
                                                            <Link to={`/admin/units/${sim.unitId}`}>
                                                                {sticker(sim.unitId)}
                                                            </Link>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>
                                                    <td>{sim.notes || "—"}</td>
                                                    <td>{formatDate(sim.addDate)}</td>
                                                </tr>
                                                {expanded && (
                                                    <tr className="units-expand-row">
                                                        <td colSpan={LIST_COLUMNS} onClick={(e) => e.stopPropagation()}>
                                                            <SimExpandPanel
                                                                sim={sim}
                                                                units={units}
                                                                onSimPatched={handleSimPatched}
                                                            />
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
